import { useState } from 'react';
import { createMatch, quickPlay } from '../../net/session';
import { useSessionStore } from '../../store/sessionStore';
import { useSettingsStore } from '../../store/settingsStore';
import { PlayVsBotsPanel } from '../PlayVsBotsPanel';

export const BOTS_UNAVAILABLE_HINT =
  'Bots were unavailable last time. Check that the bot service is running, then try again.';

/** Which button opened the pending connection, so only that one says "Connecting…". */
export type HomeAction = 'quick' | 'create' | 'bots' | 'join' | 'browser';

interface Props {
  /** The action that is connecting right now, if a Home button started it. */
  pending: HomeAction | null;
  onAction: (action: HomeAction) => void;
  isPrivate: boolean;
  setPrivate: (isPrivate: boolean) => void;
}

/** Home → Play: Quick Match, Practice vs Bots (inline) or Create Lobby (+ Private). */
export function PlayView({ pending, onAction, isPrivate, setPrivate }: Props) {
  const name = useSettingsStore((s) => s.name);
  const busy = useSessionStore((s) => s.status === 'connecting');
  const botsUnavailable = useSessionStore((s) => s.botsUnavailable);
  const [botsOpen, setBotsOpen] = useState(false);
  const label = (action: HomeAction, idle: string) =>
    busy && pending === action ? 'Connecting…' : idle;

  return (
    <div className="play-options">
      <div className="play-option">
        <button
          className="play-choice primary"
          disabled={busy}
          aria-busy={busy && pending === 'quick'}
          aria-describedby="play-quick-desc"
          autoFocus
          onClick={() => {
            onAction('quick');
            void quickPlay(name);
          }}
        >
          {label('quick', 'Quick Match')}
        </button>
        <p id="play-quick-desc" className="muted play-desc">
          Jump into a public match
        </p>
      </div>

      <div className="play-option">
        <button
          className="play-choice disclosure"
          aria-expanded={botsOpen}
          aria-describedby="play-bots-desc"
          onClick={() => setBotsOpen((open) => !open)}
        >
          Practice vs Bots
        </button>
        <p id="play-bots-desc" className="muted play-desc">
          Warm up against AI
        </p>
        {botsUnavailable && <p className="muted bots-hint">{BOTS_UNAVAILABLE_HINT}</p>}
        {botsOpen && (
          <PlayVsBotsPanel
            name={name}
            disabled={busy}
            connecting={busy && pending === 'bots'}
            onStart={() => onAction('bots')}
          />
        )}
      </div>

      <div className="play-option">
        <div className="row">
          <button
            className="play-choice grow"
            disabled={busy}
            aria-busy={busy && pending === 'create'}
            aria-describedby="play-create-desc"
            onClick={() => {
              onAction('create');
              void createMatch(name, isPrivate);
            }}
          >
            {label('create', 'Create Lobby')}
          </button>
          <label className="check" title="Only people with the code or link can join">
            <input
              type="checkbox"
              checked={isPrivate}
              disabled={busy}
              onChange={(e) => setPrivate(e.target.checked)}
            />
            Private
          </label>
        </div>
        <p id="play-create-desc" className="muted play-desc">
          Host a match for friends
        </p>
      </div>
    </div>
  );
}
